import React, { useState, useMemo } from "react";
import { TableCell, TableRow, IconButton } from "@mui/material";
import TextView from "../../../../TextView";
import WheelComponent from "../../../../WheelComponent";
import { v4 as uuidv4 } from "uuid";
import Status from "./Status";
import WheelCardThreeDotMenu from "../../../../WheelCardThreeDotMenu/WheelCardThreeDotMenu";
import {
  getStatusValue,
  getLoggedInUser,
  isUserLoggedIn,
} from "../../../../../utils/helpers";
import { getWheelMenuItems } from "../../../../../utils/wheelMenuHelpers";
import { ASSETS } from "../../../../../utils/config";
import { colors } from "../../../../../utils";
import styled from "styled-components";
import Timer from "../../../WheelListing/ListComponents/Timer";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CircularProgress } from "@mui/material";
import {
  useToggleWheelActive,
  useDeleteWheelV2,
} from "../../../../../services/mutations";
import { toast } from "react-toastify";
import IyugaTooltip from "../../../../IyugaTooltip";
import { useDispatch } from "react-redux";
import { openModal, closeModal } from "../../../../../redux/reducers/dialog";
import { useParams } from "react-router-dom";

const BlurredContent = styled.div`
  filter: ${(props) => (props.$isActive ? "blur(0px)" : "blur(8px)")};
`;

const WheelOverlay = styled.div`
  position: absolute;
  top: -15px;
  left: 0;
  width: 110px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.4);
`;

const ActionButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 6px;
    margin: 0 4px;

    &:hover {
      background-color: ${(props) =>
        props.$color === "delete"
          ? "rgba(214, 70, 70, 0.04)"
          : "rgba(129, 38, 219, 0.04)"};
    }

    .MuiSvgIcon-root {
      font-size: 20px;
      color: ${(props) => (props.$color === "delete" ? "#FC5555" : "#8F38EF")};
    }
  }
`;

const WheelTableRow = ({
  data,
  tabType,
  handleRedirection,
  formatDate,
  processText,
  processWheelData,
  isInsideFolder,
  customMenuItems,
  excludeMenuItems,
  onWheelDelete,
  onReactivate,
}) => {
  const dispatch = useDispatch();
  const [isReactivating, setIsReactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const toggleWheelActive = useToggleWheelActive();
  const deleteWheelFunc = useDeleteWheelV2();
  const { id } = useParams();
  const isOthersGallery = useMemo(
    () => parseInt(id) !== getLoggedInUser()?.id,
    [id]
  );
  const isLoggedIn = useMemo(() => isUserLoggedIn(), []);
  //   const expected_deletion_time = useMemo(() => {
  //     const lastChar = String(data?.id || '').slice(-1);
  //     const isEven = parseInt(lastChar, 16) % 2 === 0;
  //     return isEven ? "2025-04-13T15:02:00.000000Z" : "2025-04-14T12:50:00.000000Z";
  //   }, [data?.id]);
  const expected_deletion_time = data?.expected_deletion_time;

  const handleReactivate = () => {
    setIsReactivating(true);
    toggleWheelActive.mutate(data?.id, {
      onSuccess: () => {
        toast.success("Wheel reactivated successfully");
        onReactivate?.(data?.id);
      },
      onError: () => {
        toast.error("Failed to reactivate wheel");
      },
      onSettled: () => {
        setIsReactivating(false);
      },
    });
  };

  const handleDelete = () => {
    dispatch(
      openModal({
        title: "Delete Wheel",
        subtitle: "Are you sure you want to delete this Wheel?",
        onCancel: () => dispatch(closeModal()),
        onConfirm: () => {
          setIsDeleting(true);
          deleteWheelFunc.mutate(data?.id, {
            onSuccess: () => {
              toast.success("Wheel deleted successfully");
              onWheelDelete?.(data?.id);
            },
            onError: () => {
              toast.error("Failed to delete wheel");
            },
            onSettled: () => {
              setIsDeleting(false);
              dispatch(closeModal());
            },
          });
        },
      })
    );
  };

  // Handle click on the wheel - only navigate if wheel is active
  const handleWheelClick = () => {
    if (data?.is_active) {
      handleRedirection(data);
    }
  };

  return (
    <TableRow sx={{ height: 95 }}>
      <TableCell padding="none">
        <div
          onClick={handleWheelClick}
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "60px",
            cursor: data?.is_active ? "pointer" : "default",
            position: "relative",
          }}
        >
          <BlurredContent $isActive={data?.is_active}>
            <WheelComponent
              userData={{
                id: uuidv4(),
                url: data?.wheel_image || ASSETS.userPlaceholder,
              }}
              maxSelectedArcs={0}
              arcThickness={-10}
              chartData={processWheelData(data)}
              margin={25}
              setChartData={() => {}}
              containerDivStyle={{
                width: "110px",
                height: "110px",
              }}
            />
          </BlurredContent>
          {!data?.is_active && (
            <WheelOverlay>
              <Timer
                expected_deletion_time={expected_deletion_time}
                onWheelDelete={onWheelDelete}
                wheelId={data?.id}
                hideTooltip={true}
              />
            </WheelOverlay>
          )}
          <TextView
            variant="subtitle"
            style={{
              fontSize: 12,
              color: colors.primary,
              fontWeight: 600,
              marginLeft: 8,
            }}
          >
            {processText(data?.wheel_name)}
          </TextView>
        </div>
      </TableCell>
      <TableCell padding="none">
        <BlurredContent $isActive={data?.is_active}>
          <Status
            isPublic={data?.visibility === 1}
            icon={
              data?.visibility === 1
                ? ASSETS.segmentEditor.PublicWheel
                : ASSETS.segmentEditor.PrivateWheel
            }
            text={getStatusValue(data?.visibility)}
          />
        </BlurredContent>
      </TableCell>
      <TableCell padding="none">
        <BlurredContent $isActive={data?.is_active}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {data?.is_locked && (
              <img
                src={ASSETS.segmentEditor.LockWheel}
                width={18}
                height={18}
                alt="Locked Icon"
              />
            )}
            {data?.is_cloned && (
              <img
                src={ASSETS.segmentEditor.CloneWheel}
                width={18}
                height={18}
                alt="Cloned Icon"
              />
            )}
            {data?.is_live && (
              <img
                src={ASSETS.segmentEditor.LiveShare}
                width={18}
                height={18}
                alt="Live Share Icon"
              />
            )}
            {!data?.is_live && !data?.is_cloned && !data?.is_locked && (
              <div> N/A </div>
            )}
          </div>
        </BlurredContent>
      </TableCell>
      <TableCell padding="none">
        <BlurredContent $isActive={data?.is_active}>
          {data?.size || "N/A"}
        </BlurredContent>
      </TableCell>
      <TableCell padding="none">
        <BlurredContent $isActive={data?.is_active}>
          {formatDate(data?.created_at) || "N/A"}
        </BlurredContent>
      </TableCell>
      <TableCell padding="none" align="center">
        {data?.is_active ? (
          <WheelCardThreeDotMenu
            data={data}
            include={getWheelMenuItems(
              isInsideFolder
                ? data?.is_cloned
                  ? "cloned"
                  : "created"
                : tabType,
              data,
              isOthersGallery,
              isLoggedIn
            )}
            customMenuItems={customMenuItems.map((item) =>
              item.key === "remove-from-folder"
                ? { ...item, action: () => item.action(data.id) }
                : item
            )}
            exclude={excludeMenuItems}
          />
        ) : !isOthersGallery ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {isReactivating ? (
              <CircularProgress
                size={20}
                sx={{ color: "#8F38EF", margin: "0 4px" }}
              />
            ) : (
              <IyugaTooltip
                placement="top"
                tooltip="Reactivate this wheel to make it available again"
              >
                <ActionButton
                  onClick={handleReactivate}
                  disabled={isDeleting}
                  $color="reactivate"
                >
                  <RestoreIcon />
                </ActionButton>
              </IyugaTooltip>
            )}
            {isDeleting ? (
              <CircularProgress
                size={20}
                sx={{ color: "#FC5555", margin: "0 4px" }}
              />
            ) : (
              <IyugaTooltip
                placement="top"
                tooltip="Permanently delete this wheel"
              >
                <ActionButton
                  onClick={handleDelete}
                  disabled={isReactivating}
                  $color="delete"
                >
                  <DeleteOutlineIcon />
                </ActionButton>
              </IyugaTooltip>
            )}
          </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default WheelTableRow;
